import { yupResolver } from "@hookform/resolvers/yup";
import DistrictSelect from "pages/CustomerManagement/Location/DistrictSelect";
import ProvinceSelect from "pages/CustomerManagement/Location/ProvinceSelect";
import WardSelect from "./WardSelect";

import { Switch } from "antd";
import FormHubSelect from "components/Common/Form/FormHubSelect";
import FormModelDependHubSelect from "components/Common/Form/FormModelDependHubSelect";
import { TRANSFER_ORDER_TYPE } from "constants/order";
import {
  useCreateTransportLocalities,
  useUpdateTransportLocalities,
} from "hooks/transportLocalities/useTransportLocalities";
import React, { useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useToasts } from "react-toast-notifications";
import { Button, Col, Form, FormGroup, Label, Row } from "reactstrap";
import * as yup from "yup";
import MessageFormValidate from "../../components/shared-components/MessageFormValidate";
import { formConvertErrorMessage } from "../../helpers/form_helper";
import { MODE_DETAIL } from "./utils";

const TransportLocalitiesForm = ({ detailData, onSuccess, ...props }) => {
  const { orderType } = props;
  const { t } = useTranslation(["transportLocalities"]);

  const detailOptions = useMemo(() => {
    const options = {
      allowLevels: [2, 3],
      allowEdit: true,
      mode: detailData ? MODE_DETAIL.UPDATE : MODE_DETAIL.CREATE,
      isDetailLevel2: false,
    };

    const detectLevel2FromRecord =
      detailData?.is_new_address === "N" ||
      (detailData?.address_level && Number(detailData.address_level) === 2);

    switch (orderType) {
      case TRANSFER_ORDER_TYPE.RECEIVE:
        options.allowLevels = [2];
        switch (options.mode) {
          case MODE_DETAIL.CREATE:
            options.isDetailLevel2 = true;
            break;
          case MODE_DETAIL.UPDATE:
            options.isDetailLevel2 = detectLevel2FromRecord ?? false;
            break;
          default:
            break;
        }
        return options;

      case TRANSFER_ORDER_TYPE.RETURN:
        options.allowLevels = [2, 3];
        switch (options.mode) {
          case MODE_DETAIL.CREATE:
            options.isDetailLevel2 = true;
            break;
          case MODE_DETAIL.UPDATE:
            options.isDetailLevel2 = detectLevel2FromRecord ?? false;
            break;
          default:
            break;
        }
        return options;
      default:
        return options;
    }
  }, [orderType, detailData]);

  const initDefaultValues = {
    hub_id: detailData?.hub_id
      ? { value: detailData?.hub_id, label: detailData?.hub?.name }
      : null,

    transport: detailData?.transport_id
      ? {
          value: detailData?.transport_id,
          label: detailData?.transport?.name,
        }
      : null,

    province_code:
      detailData?.province_code || detailData?.province?.code
        ? {
            value: detailData?.province_code || detailData?.province?.code,
            label: detailData?.province?.name || detailData?.province_code,
          }
        : null,

    district_code:
      detailData?.district_code || detailData?.district?.code
        ? {
            value: detailData?.district_code || detailData?.district?.code,
            label: detailData?.district?.name || detailData?.district_code,
          }
        : null,

    ward_code:
      detailData?.ward_code || detailData?.ward?.code
        ? {
            value: detailData?.ward_code || detailData?.ward?.code,
            label: detailData?.ward?.name || detailData?.ward_code,
          }
        : null,

    is_noted: Boolean(detailData?.is_noted),
    is_level_2: detailOptions.isDetailLevel2,
  };

  const onTransportLocalitiesSuccess = data => {
    onSuccess?.(data.data);
  };
  const { addToast } = useToasts();
  const onErrorTransportLocalities = error => {
    if (error.from_date === 422 && error.fields.length > 0) {
      error.fields.forEach(item => {
        let fieldName = item.field;
        switch (item.field) {
          case "common":
            addToast(item.message, {
              appearance: "error",
              autoDismiss: true,
            });
            break;
          default:
            break;
        }
        setError(fieldName, {
          type: "manual",
          message: formConvertErrorMessage(item.message, t),
        });
      });
    }
  };
  const { mutate: createTransportLocalities, isLoading: isLoadingCreate } =
    useCreateTransportLocalities(
      onTransportLocalitiesSuccess,
      onErrorTransportLocalities
    );
  const { mutate: updateTransportLocalities, isLoading: isLoadingUpdate } =
    useUpdateTransportLocalities(
      onTransportLocalitiesSuccess,
      onErrorTransportLocalities
    );

  const validator = {
    hub_id: yup
      .object()
      .nullable()
      .required(t("form.validate.select", { title: t("form.hub_id") })),

    transport: yup
      .object()
      .required(t("form.validate.required", { title: t("form.transport") }))
      .nullable(),

    province_code: yup
      .object()
      .required(t("form.validate.required", { title: t("form.province_code") }))
      .nullable(),

    district_code: yup.object().nullable(),
    ward_code: yup.object().nullable(),
  };

  const validationSchema = yup.object().shape(validator);
  const mainForm = useForm({
    resolver: yupResolver(validationSchema),
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: initDefaultValues,
  });

  const {
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    getValues,
    setError,
    trigger,
    watch,
  } = mainForm;

  const handleSubmitForm = () => {
    trigger();
    handleSubmit(() => {
      const {
        transport,
        province_code,
        district_code,
        ward_code,
        hub_id,
        is_noted,
      } = getValues();

      const finalData = {
        transport_id: transport?.value,
        province_code: province_code?.value,
        district_code: district_code?.value || null,
        ward_code: ward_code?.value || null,
        hub_id: hub_id?.value,
        order_type: orderType,
        is_noted: is_noted ? 1 : 0,
        is_new_address: is_level_2 ? "N" : "O",
      };

      if (detailData) {
        finalData.transportLocalitiesId = detailData.id;
        updateTransportLocalities(finalData);
      } else {
        createTransportLocalities(finalData);
      }
    })();
  };

  const is_noted = watch("is_noted");
  const is_level_2 = watch("is_level_2");

  const handleChangeIsNoted = value => {
    setValue("is_noted", value);
  };

  const handleChangeIsLevel2 = value => {
    setValue("is_level_2", value);
    setValue("province_code", null);
    setValue("district_code", null);
    setValue("ward_code", null);
  };

  const isDisableForm =
    detailOptions?.allowEdit === false || isLoadingCreate || isLoadingUpdate;

  return (
    <Form className="p-4" onSubmit={handleSubmitForm}>
      {detailOptions.mode === MODE_DETAIL.CREATE &&
        orderType === TRANSFER_ORDER_TYPE.RETURN && (
          <Row>
            <Col md={6}>
              <FormGroup className="">
                <Label
                  className="d-flex align-items-center gap-1"
                  style={{ cursor: "pointer" }}
                  htmlFor="is_level_2"
                  onClick={() => handleChangeIsLevel2(!is_level_2)}
                >
                  <Switch
                    className=""
                    checked={is_level_2}
                    onChange={handleChangeIsLevel2}
                    disabled={isDisableForm}
                  />
                  Địa chỉ 2 cấp
                </Label>
              </FormGroup>
            </Col>
          </Row>
        )}
      <Row>
        <Col md={6}>
          <FormHubSelect
            isMyHubOnly
            isDisabled={isDisableForm}
            hubIdDefault={detailData?.hub_id}
            isRequired
            mainForm={mainForm}
            fieldName="hub_id"
            t={t}
            title={t("form.hub_id")}
            addressLevel={is_level_2 ? 2 : 3}
            provinceCode={watch("province_code")?.value}
            districtCode={
              !is_level_2 ? watch("district_code")?.value : undefined
            }
            key={`hub-select-${is_level_2}`}
          />
        </Col>

        <Col md={6}>
          <FormGroup>
            <Label htmlFor="province_code">
              Tỉnh thành
              <span className="text-danger">*</span>
            </Label>
            <Controller
              control={control}
              name="province_code"
              render={({ field }) => (
                <ProvinceSelect
                  {...field}
                  isDisabled={isDisableForm}
                  isSearchable
                  isLevel2={is_level_2}
                  onChange={e => {
                    setValue("province_code", e);
                    setValue("district_code", null);
                    setValue("ward_code", null);
                  }}
                />
              )}
            />
            {errors.province_code && (
              <MessageFormValidate error={errors.province_code?.["message"]} />
            )}
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label htmlFor="district_code">
              {is_level_2 ? "Phường xã" : "Quận huyện"}
            </Label>
            <Controller
              control={control}
              name="district_code"
              render={({ field }) => (
                <DistrictSelect
                  {...field}
                  isLevel2={is_level_2}
                  provinceId={watch("province_code")?.value}
                  isSearchable
                  isDisabled={!watch("province_code") || isDisableForm}
                  onChange={e => {
                    setValue("district_code", e);
                    setValue("ward_code", null);
                  }}
                />
              )}
            />
            {errors.district_code && (
              <MessageFormValidate error={errors.district_code?.["message"]} />
            )}
          </FormGroup>
        </Col>
        {!is_level_2 && (
          <>
            <Col md={6}>
              <FormGroup>
                <Label htmlFor="ward_code">Phường xã</Label>
                <Controller
                  control={control}
                  name="ward_code"
                  render={({ field }) => (
                    <WardSelect
                      {...field}
                      districtId={watch("district_code")?.value}
                      isSearchable
                      isDisabled={!watch("district_code") || isDisableForm}
                      orderType={orderType}
                      wardDefault={detailData?.ward}
                      districtDefault={detailData?.district}
                    />
                  )}
                />
                {errors.ward_code && (
                  <MessageFormValidate
                    error={String(errors.ward_code?.["message"])}
                  />
                )}
              </FormGroup>
            </Col>
          </>
        )}
      </Row>
      <Row>
        <Col md={6}>
          <FormModelDependHubSelect
            isRequired
            mainForm={mainForm}
            fieldName="transport"
            t={t}
            title={" ĐVVC phân bổ"}
            pathModel={"transports"}
            isFilterByHubSelect
            idHubFilterSelect={watch("hub_id")?.value}
            isDisabled={!watch("hub_id") || isDisableForm}
          />
        </Col>
        <Col md={6} className="d-flex align-items-center">
          <FormGroup className="mt-4">
            <Switch
              className="mx-1"
              checked={is_noted}
              onChange={handleChangeIsNoted}
              disabled={isDisableForm}
            />
            <Label className="mx-2" htmlFor="is_noted">
              {t("form.is_noted")}
            </Label>
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col className="text-end">
          <Button
            size="xs"
            color="primary"
            style={{ backgroundColor: "#3C64B1" }}
            onClick={handleSubmitForm}
            disabled={isDisableForm}
          >
            {detailData ? t("button.update") : t("button.add")}
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

export default TransportLocalitiesForm;
